import { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Image from 'components/Common/Image/Image'
import switchFunc from 'lib/function/switchFunc'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LandmarkIcon from 'LuxKit/Icons/custom/LandmarkIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import { getDestinationImage, isDestinationSearchItem } from 'lib/search/searchUtils'
import type SvgIcon from 'LuxKit/SvgIcon'

const ImageBlock = styled.div`
  flex-shrink: 0;
  width: ${rem(68)};
  height: ${rem(92)};
`
const IconBlock = styled.div`
  display: flex;
  width: ${rem(68)};
  height: ${rem(92)};
  padding: ${rem(30)} ${rem(18)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.default.three};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
`

const searchItemIconSwitch = switchFunc<{ Icon: typeof SvgIcon }>({
  city: { Icon: LineMapMarkerIcon },
  state: { Icon: LineMapMarkerIcon },
  province: { Icon: LineMapMarkerIcon },
  landmark: { Icon: LandmarkIcon },
  property: { Icon: LineBedIcon },
  experience: { Icon: LineCompassIcon },
  destination: { Icon: LineMapMarkerIcon },
  airport: { Icon: LinePlaneFlyIcon },
  anywhere: { Icon: LineGlobeAltIcon },
}, { Icon: LineMapMarkerIcon })

interface Props {
  imageId: string | undefined
  searchItem: App.SearchItem
}

function RecentSearchImage(props: Props) {
  const { imageId, searchItem } = props

  const displayImageId = useMemo(() => {
    if (imageId) {
      return imageId
    }

    // Check for imageId in V3 structure
    if (searchItem.searchType === 'searchItemV3' && searchItem.display.imageId) {
      return searchItem.display.imageId
    }

    if (isDestinationSearchItem(searchItem)) {
      return getDestinationImage(searchItem.value)
    }

    if (searchItem.searchType === 'searchItemV3' &&
      'placeIds' in searchItem.searchItem.extractedData &&
      searchItem.searchItem.extractedData?.placeIds &&
      searchItem.searchItem.extractedData?.placeIds.length > 0
    ) {
      return getDestinationImage(searchItem.searchItem.extractedData?.placeIds[0])
    }

    return undefined
  }, [imageId, searchItem])

  const fallbackIcon = useMemo(() => {
    let iconToSelect: string | undefined
    if (searchItem.searchType === 'searchItemV3') {
      // For V3 items, use display.searchType
      iconToSelect = searchItem.display.searchType
    } else if (searchItem.searchType === 'destination') {
      // For V1 destination items, use destinationType (city, state, province, etc.)
      iconToSelect = searchItem.destinationType
    } else {
      // For other V1 items, use searchType directly
      iconToSelect = searchItem.searchType
    }

    if (searchItem.format.mainText === 'Anywhere') {
      iconToSelect = 'anywhere'
    }

    const { Icon } = searchItemIconSwitch(iconToSelect ?? 'destination')

    return <Icon style={{ width: 32, height: 32 }} />
  }, [searchItem])

  return (
    <ImageBlock>
      {displayImageId && <Image
        fit="center"
        width={68}
        height={92}
        dpr={2}
        id={displayImageId}
      />}
      {!displayImageId && <IconBlock>
        {fallbackIcon}
      </IconBlock>}
    </ImageBlock>
  )
}

export default RecentSearchImage
